import {
  Button,
  DateRangePicker,
  DateRangePickerValue,
  Divider,
  DropdownButton,
  FlexBox,
} from '@dixa/lib-shared-habla';
import moment from 'moment-timezone';
import { useCallback, useMemo, useState } from 'react';

import { getActivityLogDateRangePresets } from '../utils/dateRangePresets';

type ActivityLogDateRangeFilterProps = {
  /** The lower bound of the range as an ISO timestamp. */
  readonly fromTs?: string;
  /** The upper bound of the range as an ISO timestamp. */
  readonly toTs?: string;
  /** Called when the selected date range changes. */
  readonly onChange: (range: {
    readonly fromTs?: string;
    readonly toTs?: string;
  }) => void;
};

/** Represents the component for filtering the activity log by date range. */
export const ActivityLogDateRangeFilter = ({
  fromTs,
  toTs,
  onChange,
}: ActivityLogDateRangeFilterProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);

  const hasValue = Boolean(fromTs || toTs);

  // Computed on each render (rather than at module load) so the presets and max
  // date stay accurate in long-lived sessions. Audit-log events are historical,
  // so the selectable range can't extend past now.
  const presets = getActivityLogDateRangePresets();
  const maxDate = moment();

  const value = useMemo<DateRangePickerValue>(() => {
    const startDate = fromTs ? moment(fromTs) : null;
    const endDate = toTs ? moment(toTs) : null;

    return {
      startDate: startDate?.isValid() ? startDate : null,
      endDate: endDate?.isValid() ? endDate : null,
    };
  }, [fromTs, toTs]);

  const handleChange = useCallback(
    ({ startDate, endDate, presetKey }: DateRangePickerValue) => {
      // If it's a preset, resolve the actual start and end dates from the preset
      // configuration rather than the raw picker values.
      if (presetKey) {
        const preset = getActivityLogDateRangePresets().find(
          ({ key }) => key === presetKey,
        );

        // Ignore an unknown preset rather than clearing the current selection.
        if (!preset) {
          return;
        }

        onChange({
          fromTs: preset.startDate.toISOString(),
          toTs: preset.endDate.toISOString(),
        });
        return;
      }

      onChange({
        fromTs: startDate?.toISOString(),
        toTs: endDate?.toISOString(),
      });
    },
    [onChange],
  );

  const handleClear = useCallback(
    () => onChange({ fromTs: undefined, toTs: undefined }),
    [onChange],
  );

  return (
    <DropdownButton
      fixed
      noArrow={false}
      buttonProps={{ variant: 'tertiary', label: hasValue ? 1 : undefined }}
      onToggle={setIsOpen}
      dropdown={
        <FlexBox flexDirection="column" width="min-content">
          {isOpen && (
            <DateRangePicker
              startDate={value.startDate}
              endDate={value.endDate}
              maxDate={maxDate}
              inputsPlacement="top"
              withDateInput
              withTimePicker
              timePickerProps={{
                start: { allowCustomTimeInput: true },
                end: { allowCustomTimeInput: true },
              }}
              presets={presets}
              onChange={handleChange}
            />
          )}

          <Divider />

          <FlexBox padding="300" flex={1} justifyContent="flex-end">
            <Button
              variant="secondary"
              disabled={!hasValue}
              onClick={handleClear}
            >
              Clear
            </Button>
          </FlexBox>
        </FlexBox>
      }
    >
      Date range
    </DropdownButton>
  );
};
